import Utils from "../utils";
import * as Events from "../utils/events";
import AssetLibrary from "../assets/asset-library";
import Base2DElement from "./base-2d-element";

const Params = {
    steeringSensitivity: 0.4,
    steeringWheelCenter: [0.5, 0.5],

    throttleCenter: [0.5, 0],
    throttleAngle: [-1.3, -0.1],

    speedParamName: {
        speed: "Speed",
        velocityX: "Velocity X",
        velocityY: "Velocity Y",
        velocityUserAxis: "Velocity",
    },
    arrowRange: { speed: [0, 1], velocityX: [-1, 1], velocityY: [-1, 1], velocityUserAxis: [-1, 1] },
    arrowAngle: [-0.75*Math.PI, 0.75*Math.PI],

    dirParamName: {
        /*carDirection: "Car<br>direction",
        userAxisDirection: "Custom<br>direction",*/
        carDirection: "Car direction",
        userAxisDirection: "Direction",
    },
};


class TerrainCarUI extends Base2DElement {

    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid)

        this._createControls();

        this._needsUpdate = {
            visible: true,
            info: true,
            partVisible: true,
        };

        this._eventsUpdate = [
            { event: 'terraincar_toggle_visibility', updateProperty: 'visible' },
            { event: 'terraincar_info_changed', updateProperty: 'info' },
            { event: 'terraincar_toggle_ui_param', updateProperty: 'partVisible' },
        ];

        for (let eventInfo of this._eventsUpdate) {
            eventInfo.updateFunction = (terrainCar) => {
                if (terrainCar.uuid === this.terrainCar) {
                    this._needsUpdate[eventInfo.updateProperty] = true;
                }
            };

            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }
    }

    _createContainer() {
        const container = document.createElement("div");
        container.classList.add("terrain-car-cnt");

        return container;
    }

    _initValues(options) {
        this.type = "2DObject";
        this.type2d = "TerrainCarUI";
        this.index = options.index;
        this.terrainCar = options.terrainCar;
    }

    _getTerrainCarData() {
        return Q3.data.assets.objects[this.terrainCar];
    }

    _getTerrainCarObject() {
        return AssetLibrary.objects[this.terrainCar];
    }

    setTerrainCar(terrainCar) {
        this.terrainCar = terrainCar;

        for (let property in this._needsUpdate) {
            this._needsUpdate[property] = true;
        }
    }

    redraw(width, height) {
        let update = this._needsUpdate;

        this._domElement.classList.toggle("landscape", width >= height);
        this._domElement.classList.toggle("portrait", width < height);

        if (update.visible) {
            this._updateAllVisibility();
            update.visible = false;
        }

        if (update.info) {
            this._updateDisplay();
            update.info = false;
        }

        if (update.partVisible) {
            this._updatePartVisibility();
            update.partVisible = false;
        }
    }

    render(width, height) {
        if (!Q3.data.layers.layers2D[this.parentLayer.uuid] || this.disabled || Utils.queryParams.has("disable_2d")) return;

        if (!this.isMounted) {
            for (let property in this._needsUpdate) {
                this._needsUpdate[property] = true;
            }

            if (width > 0 && height > 0) {
                this.isMounted = true;
            }
        }

        this.redraw(width, height);
    }

    delete() {

        for (let eventInfo of this._eventsUpdate) {
            Events.unlisten(eventInfo.event, eventInfo.updateFunction);
        }

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        // this.parentLayer.domElement.removeChild(this.domElement);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    _updateDisplay() {
        let terrainCar = this._getTerrainCarObject();
        if (!terrainCar) return;

        this._updateControls();
    }

    _createControls() {
        const container = document.createElement("div");
        container.classList.add("terrain-car-controls");

        const inputCnt = document.createElement("div");
        inputCnt.classList.add("terrain-car-input-cnt");

        const steerCnt = document.createElement("div");
        steerCnt.classList.add("terrain-car-steer-cnt");

        const throttleCnt = document.createElement("div");
        throttleCnt.classList.add("terrain-car-throttle-cnt");

        const infoCnt = document.createElement("div");
        infoCnt.classList.add("terrain-car-info-cnt");

        const speedCnt = document.createElement("div");
        speedCnt.classList.add("terrain-car-speed-cnt");

        const steerLabel = document.createElement("p");
        steerLabel.innerText = "Steering: ";

        const throttleLabel = document.createElement("div");
        throttleLabel.classList.add("throttle-label");
        //throttleLabel.innerText = "Throttle: ";
        throttleLabel.innerHTML = "Speed control";



        const steeringWheel = document.createElement("div");
        steeringWheel.classList.add("steering-wheel");

        let steerDrag = (x0, y0, x1, y1) => {
            let terrainCar = this._getTerrainCarObject();
            if (!terrainCar) return;

            let { steeringSensitivity, steeringWheelCenter } = Params;

            let rect = steeringWheel.getBoundingClientRect();
            let [cx, cy] = steeringWheelCenter;
            cx = rect.left+(rect.right-rect.left)*cx;
            cy = rect.top+(rect.bottom-rect.top)*cy;

            let angle = getRotationAroundPoint(cx, cy, x0, y0, x1, y1);

            let steering = terrainCar.steering+angle*steeringSensitivity;
            steering = Math.min(Math.max(-1, steering), 1);
            terrainCar.setSteering(steering);
        };

        let steerMouseDragging = false;
        steeringWheel.addEventListener("mousedown", (e) => steerMouseDragging = true);
        window.addEventListener("mousemove", (e) => {
            if (!steerMouseDragging) return;
            let [x0, y0, x1, y1] = getMouseMovementFromEvent(e);
            steerDrag(x0, y0, x1, y1);
        });
        window.addEventListener("mouseup", (e) => steerMouseDragging = false);

        let getTouch = (list, identifier) => {
            for (let i = 0; i < list.length; i++) {
                let touch = list.item(i);
                if (touch.identifier === identifier) {
                    return touch;
                }
            }
        };

        let steerTouchId = null;
        let steerTouchPos = null;
        steeringWheel.addEventListener("touchstart", (e) => {
            e.preventDefault();
            if (steerTouchId !== null) return;

            let touch = e.changedTouches.item(0);
            steerTouchId = touch.identifier;
            steerTouchPos = [touch.clientX, touch.clientY];
        });
        window.addEventListener("touchmove", (e) => {
            if (steerTouchId === null) return;

            let touch = getTouch(e.changedTouches, steerTouchId);
            if (touch) {
                let [x0, y0] = steerTouchPos;
                let { clientX: x1, clientY: y1 } = touch;
                steerTouchPos[0] = x1;
                steerTouchPos[1] = y1;
                steerDrag(x0, y0, x1, y1);
            }
        });
        window.addEventListener("touchend", (e) => {
            if (steerTouchId === null) return;

            let touch = getTouch(e.changedTouches, steerTouchId);
            if (touch) {
                steerTouchId = null;
                steerTouchPos = null;
            }
        });



        /*const throttle = document.createElement("div");
        throttle.classList.add("throttle");

        let draggingThrottle = false;

        throttle.addEventListener("mousedown", (e) => {
            draggingThrottle = true;
        });
        window.addEventListener("mousemove", (e) => {
            if (!draggingThrottle) return;

            let terrainCar = this._getTerrainCarObject();
            if (!terrainCar) return;

            let { throttleCenter, throttleAngle } = Params;

            let rect = throttle.getBoundingClientRect();
            let [cx, cy] = throttleCenter;
            cx = rect.left+(rect.right-rect.left)*cx;
            cy = rect.top+(rect.bottom-rect.top)*cy;

            let deltaAngle = getMouseRotationAroundPoint(e, cx, cy);

            let [minAngle, maxAngle] = throttleAngle;
            let addThrottle = deltaAngle/(maxAngle-minAngle);
            let newThrottle = terrainCar.throttle+addThrottle;
            newThrottle = Math.min(Math.max(0, newThrottle), 1);
            terrainCar.setThrottle(newThrottle);
        });
        window.addEventListener("mouseup", (e) => {
            if (!draggingThrottle) return;
            draggingThrottle = false;
        });*/

        const throttleCntCnt = document.createElement("div");
        throttleCntCnt.classList.add("throttle-cnt");

        const throttle = document.createElement("input");
        throttle.setAttribute("type", "range");
        throttle.setAttribute("min", 0);
        throttle.setAttribute("max", 1);
        throttle.setAttribute("step", "any");
        throttle.classList.add("throttle");

        throttle.addEventListener("input", (e) => {
            let terrainCar = this._getTerrainCarObject();
            if (!terrainCar) return;

            terrainCar.setThrottle(e.target.value);
        });

        throttleCntCnt.appendChild(throttle);



        const speedInfo = {
            container: document.createElement("div"),
            params: {},
        };

        speedInfo.container.classList.add("info-cnt");
        speedInfo.container.classList.add("speed");

        for (let param in Params.speedParamName) {
            let name = Params.speedParamName[param];

            let cnt = document.createElement("div");
            cnt.classList.add("param-cnt");
            cnt.classList.add("speed");

            let labelCnt = document.createElement("div");
            labelCnt.classList.add("label-cnt");

            let label = document.createElement("div");
            let valLabel = document.createElement("div");
            label.classList.add("param-name-label");
            valLabel.classList.add("speed-label");

            let dialCnt = document.createElement("div");
            let dial = document.createElement("div");
            let arrow = document.createElement("div");
            dialCnt.classList.add("dial-cnt");
            dial.classList.add("dial");
            dial.classList.add(param === "speed" ? "speed" : "velocity");
            arrow.classList.add("arrow");

            cnt.appendChild(dialCnt);
            cnt.appendChild(labelCnt);
            dialCnt.appendChild(dial);
            dialCnt.appendChild(arrow);
            dialCnt.appendChild(valLabel);
            labelCnt.appendChild(label);

            label.innerHTML = name;
            speedInfo.container.appendChild(cnt);

            if (param === 'speed') {
                speedCnt.appendChild(cnt);
            }

            speedInfo.params[param] = {
                cnt,
                labelCnt,
                label,
                dialCnt,
                dial,
                arrow,
                valLabel,
            };
        }

        const dirInfo = {
            container: document.createElement("div"),
            params: {},
        };

        dirInfo.container.classList.add("info-cnt");
        dirInfo.container.classList.add("direction");

        for (let param in Params.dirParamName) {
            let name = Params.dirParamName[param];

            let cnt = document.createElement("div");
            cnt.classList.add("param-cnt");
            cnt.classList.add("direction");

            let labelCnt = document.createElement("div");
            let label = document.createElement("div");
            labelCnt.classList.add("label-cnt");
            label.classList.add("direction-label");

            let compassCnt = document.createElement("div");
            let compass = document.createElement("div");
            let arrow = document.createElement("div");
            compassCnt.classList.add("compass-cnt");
            compass.classList.add("compass");
            arrow.classList.add("arrow");

            cnt.appendChild(labelCnt);
            labelCnt.appendChild(label);
            cnt.appendChild(compassCnt);
            compassCnt.appendChild(compass);
            compassCnt.appendChild(arrow);

            label.innerHTML = name;
            dirInfo.container.appendChild(cnt);

            dirInfo.params[param] = {
                cnt,
                label,
                compassCnt,
                compass,
                arrow,
            };
        }

        container.appendChild(inputCnt);
        inputCnt.appendChild(steerCnt);
        inputCnt.appendChild(throttleCnt);
        inputCnt.appendChild(speedCnt);
        container.appendChild(speedInfo.container);
        container.appendChild(dirInfo.container);
        //steerCnt.appendChild(steerLabel);
        steerCnt.appendChild(steeringWheel);
        throttleCnt.appendChild(throttleCntCnt);
        //throttleCnt.appendChild(throttleLabel);

        this._controls = {
            container,
            steerCnt,
            throttleCnt,
            speedInfo,
            dirInfo,
            steeringWheel,
            throttle,
        };

        this._domElement.appendChild(container);
    }

    _updateControls() {
        let terrainCar = this._getTerrainCarObject();
        if (!terrainCar) return;

        const { steeringWheel, throttle, speedInfo, dirInfo } = this._controls;

        let steerDeg = THREE.Math.radToDeg(terrainCar.steering/Params.steeringSensitivity);
        steeringWheel.style.transform = "rotate("+steerDeg+"deg)";

        /*let [minAngle, maxAngle] = Params.throttleAngle;
        let throttleRad = minAngle+terrainCar.throttle*(maxAngle-minAngle);
        let throttleDeg = THREE.Math.radToDeg(throttleRad);
        throttle.style.transform = "rotate("+throttleDeg+"deg)";*/

        throttle.value = terrainCar.throttle;

        let { arrowAngle } = Params;
        for (let name in speedInfo.params) {
            let arrowRange = Params.arrowRange[name];
            let param = speedInfo.params[name];
            let vel = terrainCar[name];
            let velKph = Math.round(terrainCar[name]*30*3.6);
            let value = vel/terrainCar.params.car.maxSpeed;
            value = (value-arrowRange[0])/(arrowRange[1]-arrowRange[0]);
            let angle = arrowAngle[0]+value*(arrowAngle[1]-arrowAngle[0]);
            let angleDeg = THREE.Math.radToDeg(angle);
            param.arrow.style.transform = "rotate("+angleDeg+"deg)";
            param.valLabel.style.color = velKph >= 0 ? '#00ee00' : '#ee0000';
            if (name !== "speed" && terrainCar.speed !== 0) {
                if (velKph < 0 && velKph > -1) velKph = -1;
                if (velKph >= 0 && velKph < 1) velKph = 1;
            }
            param.valLabel.innerHTML = velKph;
        }

        for (let name in dirInfo.params) {
            let param = dirInfo.params[name];
            let angle = terrainCar[name];
            let angleDeg = THREE.Math.radToDeg(angle);
            param.arrow.style.transform = "rotate("+angleDeg+"deg)";
        }
    }

    _updateAllVisibility() {
        let terrainCar = this._getTerrainCarObject();
        let show = terrainCar && terrainCar.visible;
        this._domElement.classList.toggle("hidden", !show);
    }

    _updatePartVisibility() {
        let terrainCar = this._getTerrainCarObject();
        if (!terrainCar) return;

        let { steerCnt: steeringWheel, throttleCnt: throttle, speedInfo, dirInfo } = this._controls;
        let { speed: { cnt: speedDial }, velocityX: { cnt: velXDial }, velocityY: { cnt: velYDial }, velocityUserAxis: { cnt: velUserAxisDial } } = speedInfo.params;
        let { carDirection: { cnt: carCompass }, userAxisDirection: { cnt: userAxisCompass } } = dirInfo.params;

        let paramCnt = { steeringWheel, throttle, speedDial, velXDial, velYDial, velUserAxisDial, carCompass, userAxisCompass };
        let uiVisible = terrainCar.params.ui.visible;

        for (let param in uiVisible) {
            let visible = uiVisible[param];
            let cnt = paramCnt[param];
            cnt.classList.toggle('hidden', !visible);
        }
    }
}

function getMouseMovementFromEvent(e) {
    let x1 = e.clientX, y1 = e.clientY;
    let x0 = x1-e.movementX, y0 = y1-e.movementY;

    return [x0, y0, x1, y1];
}

function getRotationAroundPoint(cx, cy, x0, y0, x1, y1) {
    let dir0 = new THREE.Vector3(x0-cx, y0-cy, 0).normalize();
    let dir1 = new THREE.Vector3(x1-cx, y1-cy, 0).normalize();
    let crossDir = new THREE.Vector3().crossVectors(dir0, dir1);
    let angleSin = Math.min(Math.max(-1, crossDir.z), 1);

    let angle = Math.asin(angleSin);
    return angle;
}

export default TerrainCarUI;
export { TerrainCarUI };
